import { MOCK_SETTINGS } from "@/mocks/settings";
import type { AppSettings, BusinessSettings, DocumentNumbering, GroupDefaults, GroupDraft, PublicBranding } from "@/services/types";
import { http } from "@/services/http";
import { USE_MOCK_API } from "@/services/config";

/**
 * Centralized settings service. Owns the business profile, document numbering
 * and default group policies. Every settings screen reads/writes through here —
 * persisted to PostgreSQL database via REST API.
 */

const delay = (ms = 350) => new Promise((resolve) => setTimeout(resolve, ms));

let store: AppSettings = JSON.parse(JSON.stringify(MOCK_SETTINGS));

export async function getPublicBranding(): Promise<PublicBranding> {
  if (!USE_MOCK_API) {
    try {
      return await http<PublicBranding>("/settings/public-branding");
    } catch {
      // Fallback
    }
  }
  return {
    businessName: store.business.businessName || "M.K. FINANCIAL SERVICES",
    tagline: store.business.tagline || "Kameti & Chit Fund Management Workspace",
    logoUrl: store.business.logoUrl || "",
  };
}

export async function getBusinessSettings(): Promise<BusinessSettings> {
  if (!USE_MOCK_API) {
    try {
      return await http<BusinessSettings>("/settings/business");
    } catch {
      // Fallback to local store on connection error
    }
  }
  await delay(80);
  return { ...store.business };
}

export async function getNumbering(): Promise<DocumentNumbering> {
  if (!USE_MOCK_API) {
    try {
      return await http<DocumentNumbering>("/settings/numbering");
    } catch {
      // Fallback
    }
  }
  await delay(80);
  return JSON.parse(JSON.stringify(store.numbering));
}

export async function getGroupDefaults(): Promise<GroupDefaults> {
  if (!USE_MOCK_API) {
    try {
      return await http<GroupDefaults>("/settings/defaults");
    } catch {
      // Fallback
    }
  }
  await delay(80);
  return { ...store.defaults };
}

export async function getSettings(): Promise<AppSettings> {
  const [business, defaults, numbering] = await Promise.all([
    getBusinessSettings(),
    getGroupDefaults(),
    getNumbering(),
  ]);
  return {
    business,
    defaults,
    numbering,
    updatedBy: "System",
    updatedAt: new Date().toISOString(),
  };
}

export async function updateBusinessSettings(business: BusinessSettings, actor: string): Promise<AppSettings> {
  if (!USE_MOCK_API) {
    try {
      const res = await http<{ business: BusinessSettings }>("/settings/business", {
        method: "PUT",
        body: business,
      });
      store.business = res.business;
      return getSettings();
    } catch {
      // Fallback
    }
  }
  await delay(400);
  store = {
    ...store,
    business: { ...business },
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  return JSON.parse(JSON.stringify(store));
}

export async function updateNumberingRule(
  key: DocumentNumbering["rules"][number]["id"],
  patch: Partial<{ prefix: string; padding: number; next: number; resetAnnually: boolean }>,
  actor: string
): Promise<DocumentNumbering> {
  if (!USE_MOCK_API) {
    try {
      return await http<DocumentNumbering>(`/settings/numbering/${key}`, {
        method: "PUT",
        body: patch,
      });
    } catch {
      // Fallback
    }
  }
  await delay(400);
  const rule = store.numbering.rules.find((r) => r.id === key);
  if (!rule) return JSON.parse(JSON.stringify(store.numbering));

  const nextRule = {
    ...rule,
    prefix: patch.prefix ?? rule.prefix,
    padding: patch.padding ?? rule.padding,
    resetAnnually: patch.resetAnnually ?? rule.resetAnnually,
  };
  if (patch.next !== undefined) {
    nextRule.next = Math.max(rule.next, Math.round(patch.next));
  }

  store = {
    ...store,
    numbering: {
      rules: store.numbering.rules.map((r) => (r.id === key ? nextRule : r)),
    },
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  return JSON.parse(JSON.stringify(store.numbering));
}

export async function updateGroupDefaults(defaults: GroupDefaults, actor: string): Promise<GroupDefaults> {
  if (!USE_MOCK_API) {
    try {
      return await http<GroupDefaults>("/settings/defaults", {
        method: "PUT",
        body: defaults,
      });
    } catch {
      // Fallback
    }
  }
  await delay(400);
  store = {
    ...store,
    defaults: { ...defaults },
    updatedBy: actor,
    updatedAt: new Date().toISOString(),
  };
  return { ...store.defaults };
}

export function applyDefaultsToDraft(draft: GroupDraft, defaults: GroupDefaults): GroupDraft {
  return {
    ...draft,
    reservedCycle: defaults.reservedCycle,
    commissionType: defaults.commissionType,
    commissionValue: defaults.commissionValue,
    gracePeriodDays: defaults.gracePeriodDays,
    lateFee: defaults.lateFee,
    lateFeeIsPercent: defaults.lateFeeIsPercent,
    lateFeePerDay: defaults.lateFeePerDay,
    bidStep: defaults.bidStep,
    blockOverdueBidding: defaults.blockOverdueBidding,
    dividendTiming: defaults.dividendTiming,
    auctionDay: defaults.auctionDay,
    dueDay: defaults.dueDay,
  };
}

export const settingsService = {
  getSettings,
  getBusinessSettings,
  getPublicBranding,
  getNumbering,
  getGroupDefaults,
  updateBusinessSettings,
  updateNumberingRule,
  updateGroupDefaults,
  applyDefaultsToDraft,
};